import {productInsertT, productT, productWithBrandT} from '@/lib/types/product.types';

import store from '@/lib/redux/store';

import {PRODUCT_UPDATE} from '@/lib/reducers';

import {DatabaseInstance} from './databaseClass';
import {SupabaseInstance} from './supabaseClass';

class ProductClass {
  private dispatch;
  public constructor() {
    this.dispatch = store.dispatch;
  }
  /**
   * Get all products from the database.
   * @returns {Promise<void>} - The product records.
   */
  public getProducts = async () => {
    try {
      this.setLoading(true);

      // get all products and sort by date_created
      const {data: res, error} = await SupabaseInstance.supabase
        .from('Product_Public')
        .select<string, productT>()
        .order('date_created', {ascending: false});

      if (error) {
        throw new Error(error.message);
      }

      this.setProducts(res);
      this.setLoading(false);
    } catch (error) {
      console.error('error in get products', error);
    }
  };

  /**
   * Get product by id from the database.
   * @param {string} id - The id of the product to fetch.
   * @returns {Promise<any>} - The product record.
   */
  public getProductById = async (id: number) => {
    try {
      const {data: res, error} = await SupabaseInstance.supabase
        .from('Product_Public')
        .select('*, Brand:Brand(*)')
        .eq('product_id', id)
        .returns<productWithBrandT[]>()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return res;
    } catch (error) {
      console.error('error in get product by id', error);
    }
  };

  /**
   * Creates a new product in the database.
   * @param {Object} data - The data for the new product.
   * @returns {Promise<any>} - The new product record.
   * @throws {Error} - If an error occurs during the database operation.
   * @throws {Error} - If the product already exists.
   */
  public createProduct = async (data: productInsertT) => {
    const {id} = await SupabaseInstance.getCurrentUser();

    // make sure product.category is'nt ''
    if (data.product_category === '' || data.product_subcategory === '') {
      throw new Error('Product category and subcategory cannot be empty');
    }

    const updatedData = {
      ...data,
      created_by: id,
      date_created: new Date().toDateString(),
      date_modified: new Date().toDateString(),
      modified_by: id,
      product_id: data.product_id ?? undefined,
    };

    const {data: record, error} = await SupabaseInstance.supabase.from('Product').insert(updatedData).select();

    if (error) {
      throw new Error(error.message);
    } else if (!record) {
      throw new Error('Error creating product');
    }

    // Update the product state
    this.setProducts([...store.getState().product.products, record[0]]);

    return record;
  };

  /**
   * This function updates a product in the database.
   * @param {Object} data - The data to update the product with.
   * @returns {Promise<any>} - The updated product record.
   */
  public updateProduct = async (data: productT) => {
    if (!data.product_id) {
      throw new Error('No product_id provided');
    }
    const record = await DatabaseInstance.updateRecord({
      table: 'Product',
      data,
      condition: {
        key: 'product_id',
        value: data.product_id,
      },
    });

    // Update the brand state
    const products = store.getState().product.products.map((product: productT) => {
      if (product.product_id === data.product_id) {
        return data;
      }
      return product;
    });

    this.setProducts(products);

    return record;
  };

  /**
   * This function deletes a product from the database.
   * @param {string} product_id - The product_id of the product to delete.
   * @returns {Promise<any>} - The deleted product record.
   */
  public deleteProduct = async (product_id: number) => {
    const record = await DatabaseInstance.deleteRecord({
      table: 'Product',
      condition: {
        key: 'product_id',
        value: product_id,
      },
    });

    // Update the product state
    this.setProducts(store.getState().product.products.filter(product => product.product_id !== product_id));

    return record;
  };

  /**
   * This function gets the retailer and distributor inventory of a product
   */
  public getProductInventory = async (productId: number) => {
    try {
      const {data, error} = await SupabaseInstance.supabase
        .from('Item')
        .select('*')
        .eq('fk_product_id', productId)
        .eq('item_inventory_type', 'distributor')
        .eq('item_inventory_type', 'retailer');
      // .returns<itemT[]>();

      if (error) {
        throw new Error(error.message);
      }

      const distributorInventory = data.filter(item => item.item_inventory_type === 'distributor');
      const retailerInventory = data.filter(item => item.item_inventory_type === 'retailer');

      return {distributorInventory, retailerInventory};
    } catch (error) {
      console.error('error in get product inventory', error);
    }
  };

  /**
   * Sets product state to be loading.
   * @returns void
   */
  public setLoading = async (loading: boolean) => {
    this.dispatch(
      PRODUCT_UPDATE({
        data: {
          ...store.getState().product,
          isLoading: loading,
        },
      })
    );
  };

  /**
   * This function is used to update the product state.
   * @param data - The data to update the product state with.
   * @returns void
   */
  public setProducts = async (data: productT[]) => {
    this.dispatch(
      PRODUCT_UPDATE({
        data: {
          ...store.getState().product,
          products: data,
        },
      })
    );
  };

  /**
   * This function is used to update the product state with a specific property.
   * @param prop - The property to update.
   * @param value - The value to update the property with.
   * @returns void
   */
  public setProductProp = async (prop: string, value: string | Date) => {
    this.dispatch(
      PRODUCT_UPDATE({
        data: {
          ...store.getState().product,
          [prop]: value,
        },
      })
    );
  };
}

const ProductInstance = new ProductClass();

export {ProductInstance};
